/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react/macro';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { ErrorBox } from 'components/ErrorBox';
import { Text } from 'components/Text';
import { TrackingContext } from 'context/Tracking';
import { Endpoints, post } from 'utils/api';
import { useStyles } from 'utils/useStyles';

import { QrCodeAndVerification } from './QrCodeAndVerification';
import { RecoveryCodes } from './RecoveryCodes';

const SESSION_STORAGE_STEP_KEY = 'mfaSetupStep';
const SESSION_STORAGE_RECOVERY_CODES_KEY = 'mfaRecoveryCodes';

const STEP_QR_VERIFICATION = 1;
const STEP_RECOVERY_CODES = 2;

const getStyles = () => ({
  container: css`
    display: flex;
    flex-direction: column;
    max-width: 600px;
    margin: 0 auto;
    padding: 2rem;

    @media (max-width: 550px) {
      padding: 1rem;
    }
  `,
  loadingOverlay: css`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
  `,
});

interface MfaSetupResponse {
    qrCodeDataUrl: string;
    manualEntrySecret: string;
}

type Step = typeof STEP_QR_VERIFICATION | typeof STEP_RECOVERY_CODES;

interface MfaSetupWizardProps {
  onComplete: () => Promise<void>;
  onSkip?: () => Promise<void>;
}

export const MfaSetupWizard = ({ onComplete, onSkip }: MfaSetupWizardProps) => {
  const styles = useStyles(getStyles);
  const [, { faro }] = useContext(TrackingContext);
  const [currentStep, setCurrentStep] = useState<Step>(STEP_QR_VERIFICATION);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [qrCodeData, setQrCodeData] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const hasInitialized = useRef(false);

  useEffect(() => {
    (async () => {
      try {
        const savedStep = sessionStorage.getItem(SESSION_STORAGE_STEP_KEY);
        const savedCodes = sessionStorage.getItem(SESSION_STORAGE_RECOVERY_CODES_KEY);

        if (savedStep && savedCodes) {
          setCurrentStep(Number(savedStep) as Step);
          setRecoveryCodes(JSON.parse(savedCodes));
          faro.trackEvent('mfa-setup-session-restored');
          return;
        }
      } catch (err) {
        console.error('Failed to restore MFA setup state:', err);
        sessionStorage.removeItem(SESSION_STORAGE_STEP_KEY);
        sessionStorage.removeItem(SESSION_STORAGE_RECOVERY_CODES_KEY);
      }

      if (hasInitialized.current) {
        return;
      }

      hasInitialized.current = true;
      setLoading(true);
      setError(null);

      try {
        const response = await post<MfaSetupResponse>(Endpoints.MFA_SETUP, {});

        if (!response || !response.qrCodeDataUrl || !response.manualEntrySecret) {
          throw new Error('Invalid response from server.');
        }

        setQrCodeData(response.qrCodeDataUrl);
        setSecret(response.manualEntrySecret);
      } catch (err: any) {
        hasInitialized.current = false;
        const errorMessage = err.message || 'Failed to initialize MFA setup.';
        setError(errorMessage);
        faro.trackError(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [faro]);

  const handleValidationSuccess = useCallback((codes: string[]) => {
    setRecoveryCodes(codes);
    setCurrentStep(STEP_RECOVERY_CODES);

    // Save recovery codes to sessionStorage so that if the user refreshes the page while in
    // the recovery codes step, they are not taken back to step 1 facing the error
    // "MFA is already enabled for this user"
    try {
      sessionStorage.setItem(SESSION_STORAGE_STEP_KEY, String(STEP_RECOVERY_CODES));
      sessionStorage.setItem(SESSION_STORAGE_RECOVERY_CODES_KEY, JSON.stringify(codes));
    } catch (err) {
      console.error('Failed to save MFA setup state:', err);
    }
  }, []);

  const handleSetupComplete = useCallback(async () => {
    try {
      sessionStorage.removeItem(SESSION_STORAGE_STEP_KEY);
      sessionStorage.removeItem(SESSION_STORAGE_RECOVERY_CODES_KEY);
    } catch (err) {
      console.error('Failed to clear MFA setup state:', err);
    }

    faro.trackFormSubmit('mfa-setup-complete');
    await onComplete();
  }, [faro, onComplete]);

  return (
    <div css={styles.container} role="main" aria-label="MFA setup">
      {currentStep === STEP_QR_VERIFICATION && (
        <>
          {loading && !qrCodeData && (
            <div css={styles.loadingOverlay}>
              <Text>Loading...</Text>
            </div>
          )}
          {error && !qrCodeData && <ErrorBox>{error}</ErrorBox>}
          {qrCodeData && secret && (
            <QrCodeAndVerification
              qrCodeData={qrCodeData}
              secret={secret}
              onSuccess={handleValidationSuccess}
              onSkip={onSkip}
            />
          )}
        </>
      )}
      {currentStep === STEP_RECOVERY_CODES && <RecoveryCodes recoveryCodes={recoveryCodes} onComplete={handleSetupComplete} />}
    </div>
  );
};

